/**
 * useReportPermissions Hook
 * Provides report-specific permission checking functionality
 *
 * Story 1.2: Reports Permission Model
 */

import { useAuth } from '../context/AuthContext';
import { usePermissions } from './usePermissions';

export interface ReportPermissions {
  /** Can view reports */
  canViewReports: boolean;
  /** Can export reports to PDF/Excel/CSV */
  canExportReports: boolean;
  /** Can create and save custom reports */
  canCreateCustomReports: boolean;
  /** Can view executive dashboard (Account Admin only) */
  canViewExecutiveDashboard: boolean;
  /** Can view engagement metrics (Manager+) */
  canViewEngagementReport: boolean;
  /** Can view OKR health report (Manager+) */
  canViewHealthReport: boolean;
  /** Can view user adoption report (Admin only) */
  canViewAdoptionReport: boolean;
  /** Can view team-level reports (Manager's own team) */
  canViewTeamReports: boolean;
  /** Is currently loading permissions */
  isLoading: boolean;
  /** User's role for scope-based filtering */
  userRole: string | undefined;
  /** User's ID for personal report filtering */
  userId: string | undefined;
}

/**
 * Hook for report-specific permission checks
 * Encapsulates all report permission logic in one place
 */
export function useReportPermissions(): ReportPermissions {
  const { user } = useAuth();
  const { can, isLoading } = usePermissions();

  // Role checks.
  //
  // ⚠️ There is deliberately no `isSuperAdmin` here. Eight `|| isSuperAdmin`
  // grants used to sit in this file and every one of them was unreachable
  // policy that misdescribed the system in the more dangerous direction —
  // it read as though a super_admin could see tenant reports. Two independent
  // controls say otherwise: `checkPermission` (backend `middleware/auth.ts`)
  // 403s super_admin on every report route, and `ProtectedRoute` redirects the
  // role off `/reports` to `/super-admin` before this page ever renders.
  const isAdmin = user?.role === 'account_admin';
  const isManager = user?.role === 'manager';

  // Base permissions
  const canViewReports = can('report', 'read') || isAdmin;
  const canExportReports = can('report', 'export') || isAdmin;
  const canCreateCustomReports = can('report', 'create_custom') || isAdmin;

  // Report-specific permissions based on role hierarchy
  // Account Admin can see all tenant reports
  // Manager can see their own team's reports (scoped server-side; Item B)
  // Employee can only see their own data (if granted)

  const canViewExecutiveDashboard = isAdmin;
  const canViewEngagementReport = isAdmin || isManager;
  const canViewHealthReport = isAdmin || isManager;
  const canViewAdoptionReport = isAdmin;
  const canViewTeamReports = isAdmin || isManager;

  return {
    canViewReports,
    canExportReports,
    canCreateCustomReports,
    canViewExecutiveDashboard,
    canViewEngagementReport,
    canViewHealthReport,
    canViewAdoptionReport,
    canViewTeamReports,
    isLoading,
    userRole: user?.role,
    userId: user?.id,
  };
}

/*
 * `getReportScope(userRole)` used to live here: a client-side guess at what a
 * role implies about report scope, with zero callers.
 *
 * DELETED rather than adopted, deliberately. Report scope is now decided by the
 * API and returned on the response (`scope`), and interpreted in exactly one
 * place — `describeReportScope` in `hooks/useReportData.ts`. A second,
 * role-derived answer on the client is precisely how the UI/API mismatch this
 * work exists to fix arose: it would have claimed 'platform' scope for a
 * super_admin the server 403s, and 'team' for a manager without ever knowing
 * how large that team is. Do not reintroduce it.
 */

export default useReportPermissions;
